var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { getPublishedSupportMaterialsPolicy } from "../services/PublishedBallots";
import { Box, Button, CircularProgress, Typography, Alert } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Dialog, ElectionListLayout, IconButton, SelectElection } from "@sequentech/ui-essentials";
import { stringToHtml, escapeTranslationValues, translateFromPresentation, EVotingStatus, isUndefined, EEarlyVotingPolicy, EResultsWebsiteStatus, parseResultsWebsitePolicy, formatVotingPortalDateTime, ESupportMaterialsPolicy, } from "@sequentech/ui-core";
import { AuthContext } from "../providers/AuthContextProvider";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import { styled } from "@mui/material/styles";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { selectBallotStyleByElectionId, selectFirstBallotStyle, } from "../store/ballotStyles/ballotStylesSlice";
import { selectElectionById, setElection, selectElectionIds } from "../store/elections/electionsSlice";
import { addCastVotes, parseCastVoteStatus, CastVoteStatus, selectCastVotesByElectionId, } from "../store/castVotes/castVotesSlice";
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { isApolloTransportError } from "../services/ApolloErrors";
import { useVoterContext } from "../hooks/useVoterContext";
import { isElectionOpenForVoting } from "../services/VotingAvailability";
import { SettingsContext } from "../providers/SettingsContextProvider";
import { GET_CAST_VOTES } from "../queries/GetCastVotes";
import { ElectionScreenErrorType, ElectionScreenMsgType, VotingPortalError, VotingPortalErrorType, } from "../services/VotingPortalError";
import { selectElectionEventById, setElectionEvent, } from "../store/electionEvents/electionEventsSlice";
import Stepper from "../components/Stepper";
import { isAcclaimedElectionCompleted, selectBypassChooser, setBypassChooser, } from "../store/extra/extraSlice";
import { GET_SUPPORT_MATERIALS } from "../queries/GetSupportMaterials";
import { GET_SUPPORT_MATERIALS_ACKNOWLEDGMENT } from "../queries/GetSupportMaterialsAcknowledgment";
import { setSupportMaterial } from "../store/supportMaterials/supportMaterialsSlice";
import { useElectionClassName } from "../hooks/useElectionClassName";
import { ballotTimeZones, currentElectionDates, votingClosedMessage, } from "../services/ElectionTimeZones";
// `StyledTitle`, `TitleSection`, `PageActions` and `ElectionContainer` were here.
// They are `ElectionListLayout` in `ui-essentials` now, with the class names they
// carry, so the Election Architect's Ballot Preview draws this screen's tree rather
// than a second one that a client's stylesheet would not fit.
var MaterialsGateLink = styled(RouterLink)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    color: inherit;\n    font-weight: 500;\n    text-decoration: underline;\n"], ["\n    color: inherit;\n    font-weight: 500;\n    text-decoration: underline;\n"])));
var isElectionEventOnlineVotingOpen = function (electionEvent) {
    var _a, _b;
    return (((_b = (_a = electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.status) === null || _a === void 0 ? void 0 : _a.voting_status) !== null && _b !== void 0 ? _b : EVotingStatus.CLOSED) === EVotingStatus.OPEN);
};
var isElectionEventKioskOpen = function (electionEvent) {
    var _a, _b;
    return (((_b = (_a = electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.status) === null || _a === void 0 ? void 0 : _a.kiosk_voting_status) !== null && _b !== void 0 ? _b : EVotingStatus.CLOSED) === EVotingStatus.OPEN);
};
var isElectionEventEarlyVotingOpen = function (electionEvent) {
    var _a, _b;
    return (((_b = (_a = electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.status) === null || _a === void 0 ? void 0 : _a.early_voting_status) !== null && _b !== void 0 ? _b : EVotingStatus.CLOSED) === EVotingStatus.OPEN);
};
var isResultsWebsiteEnabled = function (electionEvent) {
    var _a, _b;
    return (((_b = parseResultsWebsitePolicy((_a = electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.presentation) === null || _a === void 0 ? void 0 : _a.results_website)) === null || _b === void 0 ? void 0 : _b.status) ===
        EResultsWebsiteStatus.ENABLED);
};
var isElectionEventVotingClosed = function (electionEvent) {
    return (!isElectionEventOnlineVotingOpen(electionEvent) &&
        !isElectionEventKioskOpen(electionEvent) &&
        !isElectionEventEarlyVotingOpen(electionEvent));
};
var ElectionWrapper = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k;
    var electionId = _a.electionId, summary = _a.summary, bypassChooser = _a.bypassChooser, canVoteTest = _a.canVoteTest, materialsGate = _a.materialsGate;
    var navigate = useNavigate();
    var location = useLocation();
    var i18n = useTranslation().i18n;
    var _l = useParams(), tenantId = _l.tenantId, eventId = _l.eventId;
    var electionEvent = useAppSelector(selectElectionEventById(eventId));
    var election = useAppSelector(selectElectionById(electionId));
    var ballotStyle = useAppSelector(selectBallotStyleByElectionId(electionId));
    var castVotes = useAppSelector(selectCastVotesByElectionId(String(electionId)));
    // Nothing for an event without timezones: the card then renders as before.
    var zones = ballotTimeZones(electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.presentation, election === null || election === void 0 ? void 0 : election.presentation, ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml);
    var isAcclaimedCompleted = useAppSelector(isAcclaimedElectionCompleted(electionId));
    var _m = useState(false), visitedBypassChooser = _m[0], setVisitedBypassChooser = _m[1];
    var authContext = useContext(AuthContext);
    var globalSettings = useContext(SettingsContext).globalSettings;
    var isKiosk = authContext.isKiosk();
    var getElectionClassName = useElectionClassName()[0];
    if (!election) {
        throw new VotingPortalError(VotingPortalErrorType.INTERNAL_ERROR);
    }
    var defaultLanguageCode = (_d = (_c = (_b = election.presentation) === null || _b === void 0 ? void 0 : _b.language_conf) === null || _c === void 0 ? void 0 : _c.default_language_code) !== null && _d !== void 0 ? _d : (_f = (_e = electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.presentation) === null || _e === void 0 ? void 0 : _e.language_conf) === null || _f === void 0 ? void 0 : _f.default_language_code;
    var electionClassName = getElectionClassName(election);
    var electionStatus = election === null || election === void 0 ? void 0 : election.status;
    var areaPresentation = ((_g = summary === null || summary === void 0 ? void 0 : summary.area_presentation) !== null && _g !== void 0 ? _g : (_h = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml) === null || _h === void 0 ? void 0 : _h.area_presentation);
    var isVotingOpen = function () {
        return isElectionOpenForVoting({
            electionStatus: electionStatus,
            eventStatus: electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.status,
            channels: election.voting_channels,
            areaPresentation: areaPresentation,
            isKiosk: isKiosk,
        });
    };
    var isEarlyVotingPolicyEnabled = function () {
        var _a, _b;
        var area_presentation = ((_a = summary === null || summary === void 0 ? void 0 : summary.area_presentation) !== null && _a !== void 0 ? _a : (_b = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml) === null || _b === void 0 ? void 0 : _b.area_presentation);
        return (area_presentation === null || area_presentation === void 0 ? void 0 : area_presentation.allow_early_voting) === EEarlyVotingPolicy.ALLOW_EARLY_VOTING;
    };
    var isVotingStarted = function () {
        if (isKiosk) {
            return (electionStatus === null || electionStatus === void 0 ? void 0 : electionStatus.kiosk_voting_status) !== EVotingStatus.NOT_STARTED;
        }
        else {
            return ((electionStatus === null || electionStatus === void 0 ? void 0 : electionStatus.voting_status) !== EVotingStatus.NOT_STARTED ||
                (isEarlyVotingPolicyEnabled() &&
                    (electionStatus === null || electionStatus === void 0 ? void 0 : electionStatus.early_voting_status) !== EVotingStatus.NOT_STARTED));
        }
    };
    var isPreview = sessionStorage.getItem("isDemo") === "true";
    var canVote = function () {
        var _a;
        if (materialsGate) {
            return false;
        }
        if (!canVoteTest) {
            return false;
        }
        if (isAcclaimedCompleted) {
            return false;
        }
        if (election.num_allowed_revotes === 0) {
            return true;
        }
        return (isPreview || (castVotes.length < ((_a = election.num_allowed_revotes) !== null && _a !== void 0 ? _a : 1) && isVotingOpen()));
    };
    var onClickToVote = function () {
        if (!canVote() || (!isPreview && !isVotingOpen())) {
            console.log("cannot vote");
            return;
        }
        navigate("/tenant/".concat(tenantId, "/event/").concat(eventId, "/election/").concat(electionId, "/start").concat(location.search));
    };
    var handleClickBallotLocator = function () {
        navigate("../election/".concat(electionId, "/ballot-locator").concat(location.search));
    };
    var resultsUrl = globalSettings.RESULTS_PORTAL_URL &&
        eventId &&
        !isVotingOpen() &&
        isResultsWebsiteEnabled(electionEvent)
        ? "".concat(globalSettings.RESULTS_PORTAL_URL.replace(/\/+$/, ""), "/").concat(eventId, "/elections/").concat(electionId)
        : undefined;
    useEffect(function () {
        if (visitedBypassChooser) {
            console.log("visitedBypassChooser");
            return;
        }
        if (bypassChooser && election) {
            console.log("setVisitedBypassChooser");
            setVisitedBypassChooser(true);
            onClickToVote();
        }
    }, [bypassChooser, visitedBypassChooser, setVisitedBypassChooser, ballotStyle]);
    return (_jsx(SelectElection, { isActive: canVote(), isOpen: isVotingOpen(), title: translateFromPresentation(election, "name", i18n.language, {
            defaultLanguageCode: defaultLanguageCode,
        }) || "-", hasVoted: castVotes.length > 0, onClickToVote: canVote() ? onClickToVote : undefined, onClickBallotLocator: handleClickBallotLocator, resultsUrl: resultsUrl, electionDates: currentElectionDates((_j = summary === null || summary === void 0 ? void 0 : summary.election_dates) !== null && _j !== void 0 ? _j : (_k = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml) === null || _k === void 0 ? void 0 : _k.election_dates, electionStatus, isKiosk), isStarted: isVotingStarted(), className: electionClassName, timeZone: zones === null || zones === void 0 ? void 0 : zones.timeZone, closeTimeZone: zones === null || zones === void 0 ? void 0 : zones.closeTimeZone, formatDateTime: function (input, zone) {
            return formatVotingPortalDateTime(input, electionEvent, i18n.resolvedLanguage || i18n.language, zone);
        } }));
};
var ElectionSelectionScreen = function () {
    var _a, _b, _c, _d, _e, _f, _g;
    var _h = useTranslation(), t = _h.t, i18n = _h.i18n;
    var navigate = useNavigate();
    var location = useLocation();
    var globalSettings = useContext(SettingsContext).globalSettings;
    var isKioskVoter = useContext(AuthContext).isKiosk();
    var _j = useParams(), eventId = _j.eventId, tenantId = _j.tenantId;
    var electionEvent = useAppSelector(selectElectionEventById(eventId));
    var eventDefaultLanguageCode = (_b = (_a = electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.presentation) === null || _a === void 0 ? void 0 : _a.language_conf) === null || _b === void 0 ? void 0 : _b.default_language_code;
    var oneBallotStyle = useAppSelector(selectFirstBallotStyle);
    var electionIds = useAppSelector(selectElectionIds);
    var electionsById = useAppSelector(function (state) { return state.elections; });
    var ballotStylesById = useAppSelector(function (state) { return state.ballotStyles; });
    var dispatch = useAppDispatch();
    var _k = useState(true), canVoteTest = _k[0], setCanVoteTest = _k[1];
    var _l = useState(null), testElectionId = _l[0], setTestElectionId = _l[1];
    var castVotesTestElection = useAppSelector(selectCastVotesByElectionId(String(testElectionId || tenantId)));
    var _m = useState(false), openChooserHelp = _m[0], setOpenChooserHelp = _m[1];
    // Presentation comes from the immutable S3 publication snapshot.
    var materialsPolicy = getPublishedSupportMaterialsPolicy(oneBallotStyle === null || oneBallotStyle === void 0 ? void 0 : oneBallotStyle.ballot_eml.election_event_presentation, electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.presentation);
    var isMaterialsVisible = materialsPolicy !== ESupportMaterialsPolicy.OFF;
    var isMaterialsMandatory = materialsPolicy === ESupportMaterialsPolicy.MANDATORY_FOR_VOTING;
    var bypassChooser = useAppSelector(selectBypassChooser());
    var eventResultsUrl = globalSettings.RESULTS_PORTAL_URL &&
        eventId &&
        isResultsWebsiteEnabled(electionEvent) &&
        isElectionEventVotingClosed(electionEvent)
        ? "".concat(globalSettings.RESULTS_PORTAL_URL.replace(/\/+$/, ""), "/").concat(eventId)
        : undefined;
    var _o = useVoterContext(), data = _o.data, error = _o.error, loading = _o.loading, summaries = _o.summaries, refreshVoterStatus = _o.refetch;
    useEffect(function () {
        if (globalSettings.DISABLE_AUTH ||
            !tenantId ||
            !eventId ||
            typeof refreshVoterStatus !== "function")
            return;
        var refresh = function () {
            void refreshVoterStatus().catch(function () { return undefined; });
        };
        window.addEventListener("focus", refresh);
        return function () { return window.removeEventListener("focus", refresh); };
    }, [globalSettings.DISABLE_AUTH, tenantId, eventId, refreshVoterStatus]);
    // Materials
    var dataMaterials = useQuery(GET_SUPPORT_MATERIALS, {
        variables: {
            electionEventId: eventId || "",
            tenantId: tenantId || "",
        },
        skip: globalSettings.DISABLE_AUTH || !isMaterialsVisible, // Skip query if in demo mode
    }).data;
    var dataMaterialsAcknowledgment = useQuery(GET_SUPPORT_MATERIALS_ACKNOWLEDGMENT, {
        variables: {
            electionEventId: eventId || "",
        },
        // Support Materials writes the acknowledgment straight into the Apollo
        // cache on Continue (see SupportMaterialsScreen), so the default
        // cache-first policy already reflects it instantly on return here
        // instead of re-gating the Ballot list behind a fresh network round trip.
        skip: globalSettings.DISABLE_AUTH || !isMaterialsMandatory,
    }).data;
    // Whether we have a definitive answer yet. On a fresh page load the Apollo
    // cache starts empty, so these queries are genuinely loading for a moment -
    // default to "not yet known" rather than "not acknowledged" so voting
    // stays blocked (safe) without flashing the gate banner (misleading) for a
    // voter who has, in fact, already acknowledged.
    var hasAcknowledgmentLoaded = !isMaterialsMandatory ||
        (dataMaterialsAcknowledgment !== undefined && dataMaterials !== undefined);
    var acknowledgedDocumentIds = new Set((_d = (_c = dataMaterialsAcknowledgment === null || dataMaterialsAcknowledgment === void 0 ? void 0 : dataMaterialsAcknowledgment.get_support_materials_acknowledgment) === null || _c === void 0 ? void 0 : _c.document_ids) !== null && _d !== void 0 ? _d : []);
    // Acknowledging is per-document, so a material published after the
    // voter's last acknowledgment must gate voting again - checking the
    // acknowledged count alone would let that new material slip through.
    var hasAcknowledgedSupportMaterials = !isMaterialsMandatory ||
        ((_e = dataMaterials === null || dataMaterials === void 0 ? void 0 : dataMaterials.sequent_backend_support_material.every(function (material) { return !material.document_id || acknowledgedDocumentIds.has(material.document_id); })) !== null && _e !== void 0 ? _e : false);
    var hasPendingCastVotes = useAppSelector(function (state) {
        return Object.values(state.castVotes).some(function (votes) {
            // The cast action returns no status. Refresh that record before
            // deciding whether it was accepted or discarded asynchronously.
            return votes.some(function (vote) { return vote.status == null || vote.status === CastVoteStatus.IN_PROGRESS; });
        });
    });
    var _p = useQuery(GET_CAST_VOTES, {
        variables: { electionEventId: eventId !== null && eventId !== void 0 ? eventId : "" },
        // The bootstrap supplies the initial cast metadata. Only unresolved
        // casts need the existing narrow polling query.
        fetchPolicy: "network-only",
        skip: globalSettings.DISABLE_AUTH ||
            !eventId ||
            (!hasPendingCastVotes &&
                !(data === null || data === void 0 ? void 0 : data.cast_votes.some(function (vote) { return vote.status === CastVoteStatus.IN_PROGRESS; }))),
    }), polledCastVotes = _p.data, errorCastVote = _p.error, startCastVotePolling = _p.startPolling, stopCastVotePolling = _p.stopPolling;
    var castVotes = polledCastVotes !== null && polledCastVotes !== void 0 ? polledCastVotes : data;
    var materialsPath = "/tenant/".concat(tenantId, "/event/").concat(eventId, "/materials").concat(location.search);
    var materialsTitle = (electionEvent &&
        translateFromPresentation(electionEvent, "materialsTitle", i18n.language, {
            defaultLanguageCode: eventDefaultLanguageCode,
        })) ||
        t("materials.common.label");
    var handleNavigateMaterials = function () {
        navigate(materialsPath);
    };
    var hasNoElections = !loading && (data === null || data === void 0 ? void 0 : data.sequent_backend_election.length) === 0;
    var isPublished = !!((_g = (_f = data === null || data === void 0 ? void 0 : data.sequent_backend_election_event[0]) === null || _f === void 0 ? void 0 : _f.status) === null || _g === void 0 ? void 0 : _g.is_published);
    useEffect(function () {
        if (!dataMaterials || globalSettings.DISABLE_AUTH || !isMaterialsVisible) {
            return;
        }
        for (var _i = 0, _a = dataMaterials.sequent_backend_support_material; _i < _a.length; _i++) {
            var material = _a[_i];
            dispatch(setSupportMaterial(material));
        }
    }, [dataMaterials, globalSettings.DISABLE_AUTH, isMaterialsVisible]);
    useEffect(function () {
        var _a, _b, _c;
        if (data && data.sequent_backend_election.length > 0) {
            for (var _i = 0, _d = data.sequent_backend_election; _i < _d.length; _i++) {
                var election = _d[_i];
                dispatch(setElection(__assign(__assign({}, election), { image_document_id: "", contests: [], description: (_a = election.description) !== null && _a !== void 0 ? _a : undefined, alias: election.presentation
                        ? translateFromPresentation(election.presentation, "alias", i18n.language, {
                            defaultLanguageCode: (_c = (_b = election.presentation.language_conf) === null || _b === void 0 ? void 0 : _b.default_language_code) !== null && _c !== void 0 ? _c : eventDefaultLanguageCode,
                        })
                        : undefined })));
            }
            var foundTestElection = data.sequent_backend_election.find(function (election) {
                var _a, _b, _c;
                var name = election.presentation
                    ? translateFromPresentation(election.presentation, "name", i18n.language, {
                        defaultLanguageCode: (_b = (_a = election.presentation.language_conf) === null || _a === void 0 ? void 0 : _a.default_language_code) !== null && _b !== void 0 ? _b : eventDefaultLanguageCode,
                    })
                    : undefined;
                return (_c = name === null || name === void 0 ? void 0 : name.includes("TEST")) !== null && _c !== void 0 ? _c : false;
            });
            if (foundTestElection) {
                setCanVoteTest(false);
            }
            setTestElectionId((foundTestElection === null || foundTestElection === void 0 ? void 0 : foundTestElection.id) || null);
        }
    }, [data, dispatch, eventDefaultLanguageCode, i18n.language]);
    useEffect(function () {
        if (!testElectionId) {
            return;
        }
        setCanVoteTest(castVotesTestElection.length > 0);
    }, [castVotesTestElection, testElectionId, setCanVoteTest]);
    useEffect(function () {
        var _a;
        var record = (_a = data === null || data === void 0 ? void 0 : data.sequent_backend_election_event) === null || _a === void 0 ? void 0 : _a[0];
        if (record) {
            dispatch(setElectionEvent(record));
        }
    }, [data, dispatch]);
    useEffect(function () {
        if (castVotes === null || castVotes === void 0 ? void 0 : castVotes.cast_votes) {
            var castVoteList = castVotes.cast_votes;
            dispatch(addCastVotes(castVoteList.map(function (vote) { return (__assign(__assign({}, vote), { status: parseCastVoteStatus(vote.status) })); })));
            var hasUnresolvedCastVotes = hasPendingCastVotes ||
                castVoteList.some(function (castVote) { return castVote.status === CastVoteStatus.IN_PROGRESS; });
            if (hasUnresolvedCastVotes) {
                startCastVotePolling(globalSettings.QUERY_POLL_INTERVAL_MS);
            }
            else {
                stopCastVotePolling();
            }
        }
    }, [
        castVotes,
        hasPendingCastVotes,
        dispatch,
        globalSettings.QUERY_POLL_INTERVAL_MS,
        startCastVotePolling,
        stopCastVotePolling,
    ]);
    useEffect(function () {
        var _a, _b;
        var skipPolicy = (_b = (_a = electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.presentation) === null || _a === void 0 ? void 0 : _a.skip_election_list) !== null && _b !== void 0 ? _b : false;
        console.log("skipPolicy", skipPolicy);
        var newBypassChooser = skipPolicy &&
            1 === electionIds.length &&
            !errorCastVote &&
            (globalSettings.DISABLE_AUTH || !isUndefined(castVotes)) &&
            !!electionEvent &&
            (globalSettings.DISABLE_AUTH || !!data);
        if (newBypassChooser && !bypassChooser) {
            console.log("new baypass chooser", newBypassChooser);
            dispatch(setBypassChooser(newBypassChooser));
        }
    }, [
        castVotes,
        electionIds,
        errorCastVote,
        electionEvent,
        data,
        bypassChooser,
        dispatch,
        globalSettings.DISABLE_AUTH,
    ]);
    // warningMsg is rendered as HTML below, so its interpolated values are escaped
    var warningMsg;
    if (!globalSettings.DISABLE_AUTH) {
        var errorType = void 0;
        var alertType = void 0;
        if (error || errorCastVote) {
            errorType = (error === null || error === void 0 ? void 0 : error.message.includes("x-hasura-area-id"))
                ? ElectionScreenErrorType.NO_AREA
                : isApolloTransportError(error) || isApolloTransportError(errorCastVote)
                    ? ElectionScreenErrorType.NETWORK
                    : ElectionScreenErrorType.FETCH_DATA;
        }
        else if ((data === null || data === void 0 ? void 0 : data.sequent_backend_election_event.length) === 0) {
            errorType = ElectionScreenErrorType.NO_ELECTION_EVENT;
        }
        else if (!isPublished) {
            alertType = ElectionScreenMsgType.NOT_PUBLISHED;
        }
        else if (hasNoElections) {
            if (electionIds.length > 0)
                errorType = ElectionScreenErrorType.OBTAINING_ELECTION;
            else
                alertType = ElectionScreenMsgType.NO_ELECTIONS;
        }
        warningMsg = errorType
            ? t("electionSelectionScreen.errors.".concat(errorType), escapeTranslationValues({ electionIds: JSON.stringify(electionIds) }))
            : alertType
                ? t("electionSelectionScreen.alerts.".concat(alertType))
                : undefined;
    }
    // Another channel being open must not hide this voter's closing message.
    var allBallotsClosed = electionIds.length > 0 &&
        electionIds.every(function (id) {
            var _a, _b, _c, _d, _e, _f;
            return !isElectionOpenForVoting({
                electionStatus: (_a = electionsById[id]) === null || _a === void 0 ? void 0 : _a.status,
                eventStatus: electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.status,
                channels: (_b = electionsById[id]) === null || _b === void 0 ? void 0 : _b.voting_channels,
                areaPresentation: ((_d = (_c = summaries === null || summaries === void 0 ? void 0 : summaries[id]) === null || _c === void 0 ? void 0 : _c.area_presentation) !== null && _d !== void 0 ? _d : (_f = (_e = ballotStylesById[id]) === null || _e === void 0 ? void 0 : _e.ballot_eml) === null || _f === void 0 ? void 0 : _f.area_presentation),
                isKiosk: isKioskVoter,
            });
        });
    var closedMsg = allBallotsClosed
        ? votingClosedMessage({
            ballots: electionIds.map(function (electionId) {
                var _a, _b, _c, _d, _e;
                var ballotStyle = ballotStylesById[electionId];
                return {
                    electionDates: currentElectionDates((_b = (_a = summaries === null || summaries === void 0 ? void 0 : summaries[electionId]) === null || _a === void 0 ? void 0 : _a.election_dates) !== null && _b !== void 0 ? _b : (_c = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml) === null || _c === void 0 ? void 0 : _c.election_dates, (_d = electionsById[electionId]) === null || _d === void 0 ? void 0 : _d.status, isKioskVoter),
                    zones: ballotTimeZones(electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.presentation, (_e = electionsById[electionId]) === null || _e === void 0 ? void 0 : _e.presentation, ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml),
                };
            }),
            now: new Date(),
            t: t,
            lang: i18n.resolvedLanguage || i18n.language,
            formatDateTime: function (input, zone) {
                return formatVotingPortalDateTime(input, electionEvent, i18n.resolvedLanguage || i18n.language, zone);
            },
        })
        : undefined;
    // Block voting until we positively know the voter has acknowledged.
    var materialsGate = isMaterialsMandatory && !(hasAcknowledgmentLoaded && hasAcknowledgedSupportMaterials);
    // Only show the instruction banner once acknowledgment status is
    // positively known to be missing - never while it's still loading, so a
    // page refresh doesn't flash it before the "already acknowledged" result
    // arrives.
    var showMaterialsGateBanner = isMaterialsMandatory && hasAcknowledgmentLoaded && !hasAcknowledgedSupportMaterials;
    if (loading)
        return (_jsx(CircularProgress, { className: "election-selection-progress", "aria-label": t("a11y.loading") }));
    return (_jsx(ElectionListLayout, { steps: _jsx(Stepper, { selected: 0 }), titleAdornment: _jsxs(_Fragment, { children: [_jsx(IconButton, { buttonClassName: "screen-help-button", icon: faCircleQuestion, sx: { fontSize: "unset", lineHeight: "unset", paddingBottom: "2px" }, fontSize: "1rem", onClick: function () { return setOpenChooserHelp(true); }, ariaLabel: t("a11y.helpAbout", {
                        topic: t("electionSelectionScreen.chooserHelpDialog.title"),
                    }) }), _jsx(Dialog, { className: "screen-help-dialog election-selection-help-dialog", handleClose: function () { return setOpenChooserHelp(false); }, open: openChooserHelp, title: t("electionSelectionScreen.chooserHelpDialog.title"), ok: t("electionSelectionScreen.chooserHelpDialog.ok"), variant: "info", children: stringToHtml(t("electionSelectionScreen.chooserHelpDialog.content")) })] }), alert: warningMsg || closedMsg ? (_jsxs(_Fragment, { children: [warningMsg ? (_jsx(Alert, { className: "election-selection-warning", severity: "warning", children: stringToHtml(warningMsg) })) : null, closedMsg ? (_jsx(Alert, { className: "election-selection-closed", severity: "warning", children: closedMsg })) : null] })) : undefined, actions: _jsxs(_Fragment, { children: [eventResultsUrl ? (_jsx(Button, { className: "results-button election-event-results-button", variant: "secondary", component: "a", href: eventResultsUrl, target: "_blank", rel: "noreferrer", children: t("electionSelectionScreen.resultsButton") })) : null, isMaterialsVisible && electionEvent ? (_jsx(Button, { className: "support-materials-button", onClick: handleNavigateMaterials, children: materialsTitle })) : null] }), banner: showMaterialsGateBanner ? (_jsx(Alert, { severity: "warning", className: "materials-gate-banner", sx: { marginBottom: "16px" }, children: _jsx(Trans, { i18nKey: "electionSelectionScreen.materialsGate.instructions", values: { materialsTitle: materialsTitle }, components: {
                    MaterialsLink: (_jsx(MaterialsGateLink, { className: "materials-gate-link", to: materialsPath })),
                } }) })) : undefined, listRole: hasNoElections ? undefined : "list", children: !hasNoElections ? (electionIds.map(function (electionId) { return (_jsx(ElectionWrapper, { summary: summaries === null || summaries === void 0 ? void 0 : summaries[electionId], electionId: electionId, bypassChooser: bypassChooser, canVoteTest: canVoteTest || electionId === testElectionId, materialsGate: materialsGate }, electionId)); })) : (_jsx(Box, { className: "elections-empty", sx: { margin: "auto" }, children: _jsx(Typography, { className: "election-selection-empty", component: "div", children: stringToHtml(t("electionSelectionScreen.noResults")) }) })) }));
};
export default ElectionSelectionScreen;
var templateObject_1;
